/**
 * @file CostPriceHistoryChart.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-09
 * @modified 2026-10-20
 * @description Read-only cost-price line chart for an inventory item (rainbow
 *              gradient line, hover crosshair + price pill). Shows a 12-month
 *              window with back/forward chevrons (see useTimeWindow); every
 *              recorded cost entry within the window is marked with a dot. A
 *              carry-forward point at today extends the line to the current cost.
 *
 *   2.1.0 (Cam, 20 Oct 2026 — "Store till cost and sale price and history
 *   separate from Xero … (keep both) and offer both"): the till's (Epos Now)
 *   cost and sale price (ex VAT) are drawn as their own stepped, dashed
 *   series beside the Xero cost — never merged into it — with a legend
 *   naming each; the hover pill names the series. `till` is the till's
 *   intervals ({ from, to, cost, sale_ex_vat }); a cost hidden without
 *   costs.view comes back null and is simply not drawn.
 */

import React, { useMemo, useRef } from 'react';
import useThemeColors from '../hooks/useThemeColors';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
} from 'chart.js';
import { formatCurrency } from '../utils/currencyFormatter';
import useTimeWindow from '../hooks/useTimeWindow';
import ChartWindowNav from './ChartWindowNav';
import { tillStepPoints } from '../utils/tillPriceSeries';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Filler);

function parseDay(s) {
  return new Date(String(s).slice(0, 10) + 'T00:00:00');
}
function shortDate(d) {
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

function CostPriceHistoryChart({ points, currentCost, till = null }) {
  const hoverRef = useRef(null);

  // Full recorded series (ascending), plus a carry-forward point at today so the
  // line always reaches "now" at the current cost.
  const series = useMemo(() => {
    const asc = (points || [])
      .map(p => ({ ts: parseDay(p.effective_date).getTime(), cost: Number(p.cost), recorded: true }))
      .filter(p => Number.isFinite(p.cost))
      .sort((a, b) => a.ts - b.ts);
    if (asc.length < 1) return [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayTs = today.getTime();
    if (currentCost != null && Number.isFinite(Number(currentCost)) && asc[asc.length - 1].ts < todayTs) {
      asc.push({ ts: todayTs, cost: Number(currentCost), recorded: false });
    }
    return asc;
  }, [points, currentCost]);

  // The till's own series (Epos Now), drawn beside the Xero cost.
  const tillLines = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const cost = tillStepPoints(till, 'cost', today.getTime());
    const sale = tillStepPoints(till, 'sale_ex_vat', today.getTime());
    return {
      cost: cost.some(p => p.y !== null) ? cost : [],
      sale: sale.some(p => p.y !== null) ? sale : []
    };
  }, [till]);
  const hasTill = tillLines.cost.length > 0 || tillLines.sale.length > 0;

  const extent = useMemo(() => {
    const xs = [...series.map(p => p.ts), ...tillLines.cost.map(p => p.x), ...tillLines.sale.map(p => p.x)];
    return xs.length ? [Math.min(...xs), Math.max(...xs)] : [null, null];
  }, [series, tillLines]);
  const win = useTimeWindow(extent);

  /* Re-read on a theme change: a canvas keeps the colours it was painted
     with, so without this the chart stayed in the old palette. */
  const colors = useThemeColors({
    textPrimary: '--text-primary',
    textPrimaryRgb: '--text-primary-rgb',
    textSecondary: '--text-secondary',
    bgTertiary: '--bg-tertiary',
    tillCost: '--chart-till-cost',
    tillSale: '--chart-till-sale'
  }, {
    textPrimary: '#fff',
    textPrimaryRgb: '255, 255, 255',
    textSecondary: '#c2c2c2',
    bgTertiary: '#2f2f2f',
    tillCost: '#199e70',
    tillSale: '#c98500'
  });

  const chart = useMemo(() => {
    if (series.length < 2 && !hasTill) return null;
    const { startTs, endTs } = win;


    const RAINBOW = ['255, 122, 89', '255, 93, 177', '155, 109, 255', '74, 168, 255'];
    const rainbowGradient = (c, area, alpha) => {
      const g = c.createLinearGradient(area.left, 0, area.right, 0);
      RAINBOW.forEach((rgb, i) => g.addColorStop(i / (RAINBOW.length - 1), `rgba(${rgb}, ${alpha})`));
      return g;
    };
    const rainbowBorder = (ctx) => {
      const { chartArea, ctx: c } = ctx.chart;
      if (!chartArea) return `rgba(${RAINBOW[0]}, 1)`;
      return rainbowGradient(c, chartArea, 1);
    };

    // Keep the FULL series in the dataset and clip to the window via the x-axis
    // min/max — that way the line enters/leaves the window edges correctly. Only
    // recorded points that fall inside the window get a visible dot.
    const data = series.map(p => ({ x: p.ts, y: p.cost, recorded: p.recorded }));
    const allPts = [...data, ...tillLines.cost, ...tillLines.sale].filter(p => p.y !== null);
    const inWin = allPts.filter(p => p.x >= startTs && p.x <= endTs);
    const ys = (inWin.length ? inWin : allPts).map(p => p.y);
    const yLo = Math.min(...ys), yHi = Math.max(...ys);
    const yRange = (yHi - yLo) || Math.abs(yHi) || 1;
    const yStep = yRange > 1000 ? 200 : yRange > 400 ? 100 : yRange > 100 ? 50 : yRange > 20 ? 10 : yRange > 5 ? 5 : 1;
    let yMin = Math.floor((yLo - yRange * 0.3) / yStep) * yStep;
    let yMax = Math.ceil((yHi + yRange * 0.3) / yStep) * yStep;
    if (yLo >= 0) yMin = Math.max(0, yMin);

    // Monthly ticks across the (12-month) window.
    const ticks = [];
    const mt = new Date(startTs); mt.setDate(1); mt.setHours(0, 0, 0, 0);
    if (mt.getTime() < startTs) mt.setMonth(mt.getMonth() + 1);
    const stepMonths = 2;
    while (mt.getTime() <= endTs) { ticks.push(mt.getTime()); mt.setMonth(mt.getMonth() + stepMonths); }
    const tickFmt = (val) => new Date(val).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

    const chartData = {
      datasets: [{
        label: 'Cost',
        data,
        borderColor: rainbowBorder,
        fill: 'start',
        backgroundColor: (ctx) => {
          const { chart } = ctx; const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'transparent';
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, 'rgba(255, 93, 177, 0.30)');
          g.addColorStop(0.5, 'rgba(155, 109, 255, 0.12)');
          g.addColorStop(1, 'rgba(74, 168, 255, 0)');
          return g;
        },
        // A dot on every recorded entry inside the window; carry-forward + any
        // out-of-window points stay invisible.
        pointRadius: (ctx) => {
          const p = ctx.raw;
          return (p && p.recorded && p.x >= startTs && p.x <= endTs) ? 2.6 : 0;
        },
        pointHoverRadius: 0,
        pointBackgroundColor: colors.textPrimary,
        pointBorderWidth: 0,
        borderWidth: 1.5,
        borderCapStyle: 'round',
        tension: 0.4,
        spanGaps: true
      }, ...[
        ['Till cost (Epos Now)', tillLines.cost, colors.tillCost],
        ['Till sale ex VAT (Epos Now)', tillLines.sale, colors.tillSale]
      ].filter(([, pts]) => pts.length).map(([label, pts, color]) => ({
        label,
        data: pts,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        stepped: true,
        borderDash: [5, 3],
        pointRadius: (ctx) => {
          const p = ctx.raw;
          return (p && p.recorded && p.x >= startTs && p.x <= endTs) ? 2.6 : 0;
        },
        pointHoverRadius: 0,
        pointBackgroundColor: color,
        pointBorderWidth: 0,
        borderWidth: 1.5,
        spanGaps: false
      }))]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      layout: { padding: { top: 22, left: 0, right: 0, bottom: 0 } },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      onHover: (e, els, c) => {
        if (!e || e.x == null || !els || !els.length) {
          if (hoverRef.current) { hoverRef.current = null; c.draw(); }
          return;
        }
        // Ignore points outside the visible window when snapping the crosshair.
        let best = null, bd = Infinity;
        for (const a of els) {
          const el = c.getDatasetMeta(a.datasetIndex).data[a.index];
          const dp = c.data.datasets[a.datasetIndex]?.data?.[a.index];
          if (!dp || dp.y === null || dp.x < startTs || dp.x > endTs) continue;
          const d = Math.abs(el.x - e.x);
          if (d < bd) { bd = d; best = { datasetIndex: a.datasetIndex, index: a.index }; }
        }
        hoverRef.current = best;
        c.draw();
      },
      scales: {
        x: {
          type: 'linear', min: startTs, max: endTs,
          grid: { display: false }, border: { display: false },
          afterBuildTicks: (axis) => { axis.ticks = ticks.map(v => ({ value: v })); },
          ticks: { color: colors.textSecondary, font: { size: 10 }, maxRotation: 0, autoSkip: false, callback: tickFmt }
        },
        y: { display: false, min: yMin, max: yMax }
      }
    };

    const crosshair = {
      id: 'costCrosshair',
      afterDatasetsDraw(c) {
        const hit = hoverRef.current;
        if (!hit) return;
        const el = c.getDatasetMeta(hit.datasetIndex)?.data?.[hit.index];
        const dp = c.data.datasets[hit.datasetIndex]?.data?.[hit.index];
        if (!el || !dp) return;
        const { ctx, chartArea } = c;
        const x = el.x;

        ctx.save();
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, `rgba(${colors.textPrimaryRgb}, 0.55)`);
        g.addColorStop(1, `rgba(${colors.textPrimaryRgb}, 0)`);
        ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.lineWidth = 1; ctx.strokeStyle = g;
        ctx.moveTo(x, chartArea.bottom); ctx.lineTo(x, chartArea.top); ctx.stroke(); ctx.setLineDash([]);

        ctx.beginPath(); ctx.fillStyle = colors.textPrimary; ctx.arc(x, el.y, 3.5, 0, Math.PI * 2); ctx.fill();

        // With the till's series drawn too, the pill names the series it is on.
        const name = c.data.datasets.length > 1
          ? `${hit.datasetIndex === 0 ? 'Xero cost' : (c.data.datasets[hit.datasetIndex].label || '').replace(' (Epos Now)', '')}  ` : '';
        const label = `${name}${formatCurrency(dp.y)}  ·  ${shortDate(new Date(dp.x))}`;
        ctx.font = '600 11px sans-serif';
        const padX = 9;
        const w = ctx.measureText(label).width + padX * 2;
        const h = 20;
        let px = x - w / 2;
        px = Math.max(chartArea.left, Math.min(px, chartArea.right - w));
        const py = Math.max(0, chartArea.top - h - 4);
        const r = 10;
        ctx.beginPath();
        ctx.moveTo(px + r, py);
        ctx.arcTo(px + w, py, px + w, py + h, r);
        ctx.arcTo(px + w, py + h, px, py + h, r);
        ctx.arcTo(px, py + h, px, py, r);
        ctx.arcTo(px, py, px + w, py, r);
        ctx.closePath();
        ctx.fillStyle = colors.bgTertiary;
        ctx.fill();
        ctx.fillStyle = colors.textPrimary;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(label, px + padX, py + h / 2 + 0.5);
        ctx.restore();
      }
    };

    return { data: chartData, options, plugins: [crosshair] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, tillLines, hasTill, win.startTs, win.endTs, colors]);

  if (!chart) return null;
  return (
    <>
      <div className="cost-history-chart">
        <Line data={chart.data} options={chart.options} plugins={chart.plugins} />
        <ChartWindowNav
          canBack={win.canBack}
          canForward={win.canForward}
          onBack={win.goBack}
          onForward={win.goForward}
          label={win.label}
        />
      </div>
      {/* Two or more series: a legend, so no line is told apart by colour alone. */}
      {hasTill && (
        <ul className="cost-history-legend">
          {series.length >= 2 && <li><span className="swatch xero" />Xero cost</li>}
          {tillLines.cost.length > 0 && <li><span className="swatch till-cost" />Till cost (Epos Now)</li>}
          {tillLines.sale.length > 0 && <li><span className="swatch till-sale" />Till sale ex VAT (Epos Now)</li>}
        </ul>
      )}
    </>
  );
}

export default CostPriceHistoryChart;
